import React, { memo, useCallback, useMemo, useState } from 'react'
import freezeR from 'utils/freezeR'
import { Button } from 'reactstrap'
import { useParams, useHistory } from 'react-router-dom'
import { connect } from 'react-redux'
import { compose } from 'redux'
import { createStructuredSelector } from 'reselect'
import _ from 'lodash'
import { camelize } from 'humps'
import moment from 'moment'
import { extractDateTimeInfo } from 'utils/commonFunctions'

import { openError } from 'containers/App/reducers'
import Header from '../components/Header'
import ImportExportInputRow from '../components/ImportExportInputRow'
import ImportExportSelectRow from '../components/ImportExportSelectRow'
import PrevStartButton from '../components/PrevStartButton'
import PageNumber from '../components/PageNumber'
import { updateBooking, resetAllData, startLoading, endLoading } from '../reducer'
import * as S from '../style'
import type { inputType, GeneralBookingType, GeneralMasterDataType } from '../type'
import { INTEGRATED_RECEPTION_URL_BASE } from '../constants'
import { useDispatch } from 'reduxStoreHelper'
import { TEN_DIGITS } from 'constants/misc'
import { postGeneralBookingCheckin, updateGeneralBookingCheckinWithBookingNo } from '../api'
import { masterDataSelectors, driverConfirmationsSelectors } from '../selectors'
import { trimData, getStatusCodesAndErrorCode } from 'utils/commonFunctions'
import { isSelectType } from 'services/Booking'
import type { ParameterItemType, ReceptionOptionType } from '../type'
import { ADDITIONAL_COLUMNS_RANGE } from 'constants/ReceptionCustom'
import { ImportFlagCheck } from '../constants'
import type { ConfirmationType } from 'containers/IntegratedReceptionCustom/type'
import { EntryInstructionTimeReceptionForm } from 'components/EntryInstructionTimeReceptionForm'
import { DEFAULT_OTHER_OPTION_SELECT } from 'components/SelectOptions'

const IMPORT_EXPORT_TYPES: inputType[] = freezeR([
  {
    id: 'import',
    label: '荷降ろし',
  },
  {
    id: 'export',
    label: '荷積み',
  },
])

const IMPORT = 'import'
const EXPORT = 'export'

const REGEX_IMPORT = /^import/

type Props = {
  booking: GeneralBookingType
  isFromBookingNo: boolean
  canGeneralBooking?: boolean
  driverConfirmations?: ConfirmationType[]
  masterData: GeneralMasterDataType
}

const EntryLoadPage: React.FC<Props> = ({
  booking,
  isFromBookingNo,
  canGeneralBooking,
  driverConfirmations,
  masterData,
}) => {
  const history = useHistory()
  const { generalReceptionNumber }: { generalReceptionNumber: string } = useParams()
  const dispatch = useDispatch()
  const packageType: any = _.get(masterData, 'packageTypes', [])
  const tenantParameters = _.get(masterData, 'tenantParameters')
  const importParams = _.get(masterData, 'tenantParameters.import')

  const entryInstructionTimeParameter = tenantParameters.base?.find(
    (item) => item.columnName === 'general_entry_instruction_time',
  )
  const isEntryInstructionTimeRequired = entryInstructionTimeParameter?.requiredFlag ?? false
  const isEntryInstructionTimeDisplayed = entryInstructionTimeParameter?.displayFlag ?? false
  const { defaultInstructionDate, defaultInstructionHours, defaultInstructionMinutes } =
    useMemo(() => {
      const defaultInstructionTime = booking.generalEntryInstructionTime
      if (!defaultInstructionTime) {
        return {
          defaultInstructionDate: '',
          defaultInstructionHours: '',
          defaultInstructionMinutes: '',
        }
      }
      const dateTimeInfo = extractDateTimeInfo(
        moment(defaultInstructionTime).format('YYYY-MM-DD HH:mm'),
      )
      return {
        defaultInstructionDate: dateTimeInfo?.date || '',
        defaultInstructionHours: dateTimeInfo?.hour || '',
        defaultInstructionMinutes: dateTimeInfo?.minute || '',
      }
    }, [booking.generalEntryInstructionTime])
  const [entryInstructionDate, setEntryInstructionDate] = useState(defaultInstructionDate)
  const [entryInstructionHours, setEntryInstructionHours] = useState(defaultInstructionHours)
  const [entryInstructionMinutes, setEntryInstructionMinutes] = useState(defaultInstructionMinutes)
  const [entryInstructionTimeError, setEntryInstructionTimeError] = useState({
    date: false,
    hours: false,
    minutes: false,
    message: '',
  })
  const validateEntryInstructionTime = useCallback((): boolean => {
    const isDateEmpty =
      !entryInstructionDate || entryInstructionDate === DEFAULT_OTHER_OPTION_SELECT
    const isHoursEmpty =
      !entryInstructionHours || entryInstructionHours === DEFAULT_OTHER_OPTION_SELECT
    const isMinutesEmpty =
      !entryInstructionMinutes || entryInstructionMinutes === DEFAULT_OTHER_OPTION_SELECT
    let isValid = true
    const errorMessage = '入場指示時刻を正しく入力してください'

    // 必須の場合、全ての項目が埋まっていないといけない
    if (isEntryInstructionTimeRequired) {
      if (isDateEmpty || isHoursEmpty || isMinutesEmpty) {
        setEntryInstructionTimeError({
          date: !entryInstructionDate,
          hours: !entryInstructionHours,
          minutes: !entryInstructionMinutes,
          message: errorMessage,
        })
        isValid = false
      } else {
        setEntryInstructionTimeError({
          date: false,
          hours: false,
          minutes: false,
          message: '',
        })
      }
    }

    // 必須ではない場合、全ての項目が空もしくは全ての項目が埋まっている場合はエラーにならない
    if (!isEntryInstructionTimeRequired) {
      if (
        (isDateEmpty && isHoursEmpty && isMinutesEmpty) ||
        (!isDateEmpty && !isHoursEmpty && !isMinutesEmpty)
      ) {
        setEntryInstructionTimeError({
          date: false,
          hours: false,
          minutes: false,
          message: '',
        })
      } else {
        setEntryInstructionTimeError({
          date: isDateEmpty,
          hours: isHoursEmpty,
          minutes: isMinutesEmpty,
          message: errorMessage,
        })
        isValid = false
      }
    }
    return isValid
  }, [
    entryInstructionDate,
    entryInstructionHours,
    entryInstructionMinutes,
    isEntryInstructionTimeRequired,
  ])

  const handleChangeEntryInstructionTime = useCallback(
    (value: string, name: 'date' | 'hours' | 'minutes') => {
      const newDate = name === 'date' ? value : entryInstructionDate
      const newHours = name === 'hours' ? value : entryInstructionHours
      const newMinutes = name === 'minutes' ? value : entryInstructionMinutes

      if (name === 'date') {
        setEntryInstructionDate(value)
      } else if (name === 'hours') {
        setEntryInstructionHours(value)
      } else if (name === 'minutes') {
        setEntryInstructionMinutes(value)
      }

      const isValidDate = newDate && newDate !== DEFAULT_OTHER_OPTION_SELECT
      const isValidHours = newHours && newHours !== DEFAULT_OTHER_OPTION_SELECT
      const isValidMinutes = newMinutes && newMinutes !== DEFAULT_OTHER_OPTION_SELECT

      const entryInstructionTime =
        isValidDate && isValidHours && isValidMinutes ? `${newDate} ${newHours}:${newMinutes}` : ''

      dispatch(updateBooking({ generalEntryInstructionTime: entryInstructionTime }))
    },
    [dispatch, entryInstructionDate, entryInstructionHours, entryInstructionMinutes],
  )

  const importTenantParameters = _.chain(importParams)
    .filter((item) => item.displayFlag)
    .sortBy((item) => item.displayOrder)
    .value()

  const isImportExportChecked = !!booking.importFlag || !!booking.exportFlag

  const filteredConfirmations = driverConfirmations?.filter(
    (confirmation: ConfirmationType) => confirmation?.displayFlag,
  )

  const handleClickButtonExit = useCallback(() => {
    dispatch(resetAllData())
    history.push(`${INTEGRATED_RECEPTION_URL_BASE}/${generalReceptionNumber}`)
  }, [dispatch, generalReceptionNumber, history])

  const convertToOptions = (items = [], isAdditionalColumn = false, value: any) => {
    const propName = isAdditionalColumn ? 'parameterName' : 'parameterId'
    const options = items
      //@ts-ignore
      .filter(
        (item: ReceptionOptionType) => (value && item[propName] === value) || item.displayFlag,
      )
      .map((item: ReceptionOptionType) => ({
        //@ts-ignore
        value: item[propName],
        label: item.parameterName,
      }))

    return options
  }

  const convertImportExportColumns = (booking: { [key: string]: any }) => {
    const additionalColumns: { [key: string]: any } = ADDITIONAL_COLUMNS_RANGE.reduce(
      (ac, id) => ({
        ...ac,
        [`additionalColumn${id}`]: _.get(masterData, `additionalColumn${id}`, []),
      }),
      {},
    )

    return ADDITIONAL_COLUMNS_RANGE.reduce(
      (ac, id) => ({
        ...ac,
        [`importAdditionalColumn${id}`]: convertToOptions(
          additionalColumns[`additionalColumn${id}`],
          true,
          booking[`importAdditionalColumn${id}`],
        ),
        [`exportAdditionalColumn${id}`]: convertToOptions(
          additionalColumns[`additionalColumn${id}`],
          true,
          booking[`exportAdditionalColumn${id}`],
        ),
      }),
      {},
    )
  }

  // check parameter item require and not have value
  const validateRequiredItems = (parameters: ParameterItemType[], booking: GeneralBookingType) =>
    !parameters.some(
      //@ts-ignore
      ({ requiredFlag, columnName } = {}) =>
        //@ts-ignore
        requiredFlag && !booking[camelize(columnName)]?.toString()?.trim(),
    )

  const canClickNextScreen = () => {
    const isImportExportEnable = tenantParameters?.base?.some(
      (item: { columnName: string; requiredFlag: any }) =>
        item.columnName === 'import_export_enable' && item.requiredFlag,
    )

    if (isImportExportEnable && !booking.importFlag && !booking.exportFlag) {
      return false
    }
    const isImportValid =
      booking.importFlag === 1 ? validateRequiredItems(tenantParameters.import, booking) : true
    const isExportValid =
      booking.exportFlag === 1 ? validateRequiredItems(tenantParameters.export, booking) : true
    return isImportValid && isExportValid
  }

  const handleUpdateBooking = (
    value: any,
    importOrExport: string,
    columnName: string,
    maxLength?: number,
  ) => {
    let newValue = value

    const name = camelize(
      importOrExport === IMPORT ? columnName : columnName.replace(REGEX_IMPORT, EXPORT),
    )

    if (['importGoodsAmount', 'exportGoodsAmount'].includes(name)) {
      const num = value.replace(/[^0-9]/g, '')

      if (num > TEN_DIGITS) return

      newValue = num ? Number(num) : ''
    } else {
      if (maxLength && newValue.length > maxLength) {
        newValue = newValue.substr(0, maxLength)
      }
    }
    dispatch(
      updateBooking({
        [name]: newValue,
      }),
    )
  }

  const handleClearEntryInstructionTime = () => {
    setEntryInstructionDate('')
    setEntryInstructionHours('')
    setEntryInstructionMinutes('')
    setEntryInstructionTimeError({
      date: false,
      hours: false,
      minutes: false,
      message: '',
    })
    dispatch(updateBooking({ generalEntryInstructionTime: '' }))
  }

  const getAdditionalColumnsMaxLength = () =>
    ADDITIONAL_COLUMNS_RANGE.reduce(
      (ac, id) => ({
        ...ac,
        [`import_additional_column${id}`]: 200,
      }),
      {},
    )

  const getMaxLengthOfInput: { [key: string]: number } = {
    import_goods_owner: 255,
    import_goods_name: 255,
    import_goods_amount: 10,
    import_main_goods: 50,
    import_delivery_slip_number: 50,
    import_contact_notes: 255,
    ...getAdditionalColumnsMaxLength(),
  }

  const handleSubmit = async () => {
    if (!validateEntryInstructionTime()) {
      return
    }

    // フォーマットを変換してリクエストを送信
    const formattedTime = booking.generalEntryInstructionTime
      ? moment(booking.generalEntryInstructionTime).isValid()
        ? moment(booking.generalEntryInstructionTime).format('YYYY-MM-DD HH:mm')
        : ''
      : '';
    booking.generalEntryInstructionTime = formattedTime

    const bookingFormat = trimData(booking)

    dispatch(startLoading())
    if (isFromBookingNo) {
      try {
        const resp = await updateGeneralBookingCheckinWithBookingNo(bookingFormat)
        history.push({
          pathname: `${INTEGRATED_RECEPTION_URL_BASE}/entry-finish/${generalReceptionNumber}`,
          state: {
            portalFlag: resp?.data?.portalFlag,
            lineFlag: resp?.data?.lineFlag,
          },
        })
      } catch (error) {
        const { errorCodes, status } = getStatusCodesAndErrorCode(error)
        //@ts-ignore
        dispatch(openError(error.message, true, errorCodes, status))
      } finally {
        dispatch(endLoading())
      }
    } else {
      try {
        const resp = await postGeneralBookingCheckin(bookingFormat)
        history.push({
          pathname: `${INTEGRATED_RECEPTION_URL_BASE}/entry-finish/${generalReceptionNumber}`,
          state: {
            portalFlag: resp?.data?.portalFlag,
            lineFlag: resp?.data?.lineFlag,
          },
        })
      } catch (error) {
        const { errorCodes, status } = getStatusCodesAndErrorCode(error)
        //@ts-ignore
        dispatch(openError(error.message, true, errorCodes, status))
      } finally {
        dispatch(endLoading())
      }
    }
  }

  const renderImportExportInput = (importExportType: inputType) => {
    const optionMap = {
      importPackageType: convertToOptions(packageType, false, booking.importPackageType),
      exportPackageType: convertToOptions(packageType, false, booking.exportPackageType),
      ...convertImportExportColumns(booking),
    }
    return importTenantParameters.map((parameter: ParameterItemType) => {
      const columnName = camelize(
        importExportType.id === IMPORT
          ? parameter.columnName
          : parameter.columnName.replace(REGEX_IMPORT, EXPORT),
      )

      if (isSelectType(parameter)) {
        return (
          <ImportExportSelectRow
            key={parameter.columnName}
            id={parameter.id}
            label={parameter.displayName}
            value={_.get(booking, `${columnName}`)}
            optionMap={optionMap}
            columnName={columnName}
            requiredFlag={parameter.requiredFlag}
            onChange={(value: string | number) =>
              handleUpdateBooking(value, importExportType.id, columnName)
            }
            data-dg-id={`entry-load-page-tenant-parameter-${parameter.id}`}
          />
        )
      }
      return (
        <ImportExportInputRow
          key={parameter.columnName}
          id={parameter.id}
          label={parameter.displayName}
          value={_.get(booking, `${columnName}`)}
          requiredFlag={parameter.requiredFlag}
          onChange={(value: string | number) =>
            handleUpdateBooking(
              value,
              importExportType.id,
              columnName,
              getMaxLengthOfInput[parameter.columnName],
            )
          }
          data-dg-id={`entry-load-page-tenant-parameter-${parameter.id}`}
        />
      )
    })
  }

  return (
    <S.Wrapper>
      <Header />
      <S.EntryLoadPage className="pt-3 px-3">
        <div className="bg-white px-3 pb-3" style={{ borderRadius: 12 }}>
          <div className="text-center mb-3">
            <div className="text-size32 font-weight-bold" data-dg-id="entry-load-page-title">
              入庫/出庫情報を入力
            </div>
          </div>
          {isEntryInstructionTimeDisplayed && (
            <EntryInstructionTimeReceptionForm
              generalBookingNo={booking.generalBookingNo}
              entryInstructionDate={entryInstructionDate}
              entryInstructionHours={entryInstructionHours}
              entryInstructionMinutes={entryInstructionMinutes}
              isEntryInstructionTimeRequired={isEntryInstructionTimeRequired}
              handleClearEntryInstructionTime={handleClearEntryInstructionTime}
              errors={entryInstructionTimeError}
              handleChange={handleChangeEntryInstructionTime}
            />
          )}
          <S.EntryLoadPageContent isImportExportChecked={isImportExportChecked}>
            {IMPORT_EXPORT_TYPES.map((item: inputType) => (
              <S.EntryLoadPageArea hasBorderRight={item.id === IMPORT} key={item.id}>
                <S.EntryLoadPageAreaContent
                  data-dg-id={`entry-load-page-tenant-parameters-${item.id}`}
                  hasBorderRight={item.id === IMPORT} className="p-4">
                  <S.CheckboxCustom
                    id={`entry-load-page-checkbox-${item.id}`}
                    isGray={false}
                    label={item.label}
                    className="mb-3"
                    checked={(item.id === IMPORT ? booking?.importFlag : booking?.exportFlag) === 1}
                    onChange={(e: any) => {
                      handleUpdateBooking(
                        e?.target?.checked ? ImportFlagCheck.CHECK : ImportFlagCheck.UNCHECK,
                        IMPORT,
                        `${item.id}Flag`,
                      )
                    }}
                  />
                  <S.ImportExportContent
                    isShow={(item.id === IMPORT ? booking?.importFlag : booking?.exportFlag) === 1}
                  >
                    {renderImportExportInput(item)}
                  </S.ImportExportContent>
                </S.EntryLoadPageAreaContent>
              </S.EntryLoadPageArea>
            ))}

            <div
              className="d-flex justify-content-between p-3"
              style={{
                position: 'fixed',
                backgroundColor: '#ededed',
                left: 0,
                right: 0,
                bottom: 0,
              }}
            >
              <div style={{ width: '33%' }}>
                <PrevStartButton
                  data-dg-id="entry-load-page-button-back"
                  onClick={handleClickButtonExit} />
              </div>
              <div className="text-center" style={{ width: '34%' }}>
                <PageNumber
                  page="entry-load"
                  existsDriverConfirmations={Boolean(filteredConfirmations?.length)}
                  isFreePlan={!canGeneralBooking}
                />
              </div>
              <div className="text-right" style={{ width: '33%' }}>
                <Button
                  outline
                  color="primary"
                  data-dg-id="entry-load-page-button-back"
                  className="mr-3 rounded-pill bg-white px-4 text-primary"
                  onClick={() =>
                    history.push(
                      `${INTEGRATED_RECEPTION_URL_BASE}/entry-driver-info/${generalReceptionNumber}`,
                    )
                  }
                >
                  もどる
                </Button>

                {Boolean(filteredConfirmations?.length) &&
                  (canClickNextScreen() ? (
                    <Button
                      data-dg-id="entry-load-page-button-next"
                      color="primary"
                      className="rounded-pill px-md-4 px-sm-1 px-0"
                      onClick={() =>
                        history.push(
                          `${INTEGRATED_RECEPTION_URL_BASE}/entry-confirmation/${generalReceptionNumber}`,
                        )
                      }
                    >
                      次へ
                    </Button>
                  ) : (
                    <Button
                      data-dg-id="entry-load-page-button-next">
                      color="primary"
                      className="rounded-pill px-md-4 px-sm-1 px-0"
                      disabled
                      次へ
                    </Button>
                  ))}

                {!filteredConfirmations?.length && (
                  <Button
                    data-dg-id="entry-load-page-button-complete"
                    color="primary"
                    className="rounded-pill px-md-4 px-sm-1 px-0"
                    disabled={!canClickNextScreen()}
                    onClick={handleSubmit}
                  >
                    登録完了
                  </Button>
                )}
              </div>
            </div>
          </S.EntryLoadPageContent>
        </div>
      </S.EntryLoadPage>
    </S.Wrapper>
  )
}

const mapStateToProps = createStructuredSelector({
  driverConfirmations: driverConfirmationsSelectors(),
  masterData: masterDataSelectors(),
})

const withConnect = connect(mapStateToProps, null)

export default compose(withConnect)(memo(EntryLoadPage))
